import { autoinject, computedFrom, observable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import * as numeral from 'numeral';
import * as moment from 'moment';
import 'moment-timezone';
import { AutopayFrequencyOption } from '../../models/autopay';
import { Ach, AchAccountType, Autopay, Card, PaymentMethodType } from '../../models/autopay/';
import { Account, Customer } from '../../models/customer';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { Portfolio } from '../../models/portfolio';
import { TemplateData } from '../../models/template-data';
import { State } from '../../services/global-state';
import { HPFServiceClient } from '../../services/hpfApi';
import { PayrazrService } from '../../services/payrazr';
import { AutopayService } from './autopay.service';
import { FeeCalculationValue } from '../../models/feeCalculationValue';
import { PaymentPlanType } from '../../models/paymentPlanType';

@autoinject
export class SetupAutopay {
	account: Account;
	customer: Customer;
	portfolio: Portfolio;
	isLoading = true;
	isSubmitting = false;
	validationErrors: Array<string>;
	state: string = 'setup';
	confirmationTemplateData: TemplateData;
	confirmationTitle: string;
	error;

	amount;
	paymentInformation;
	endDateOption: string = 'indefinite';
	paymentAmount: string;
	payInFull: boolean = true;
	dayOfMonth: number;
	lastPaymentDate: Date;
	lastPaymentPayments: number = 0;
	selectedFrequency: AutopayFrequencyOption;
	availableFrequencies: Array<AutopayFrequencyOption>;
	agreeToTerms: boolean;
	minFutureStartDateDays: number;
	maxFutureStartDateDays: number;
	calculatedFeeAmount: number;
	calculatedTotalAmount: number;
	serviceFeeEnabled: boolean = false;
	cardNumber: string;
	cardInfoKey: string;
	daysOfMonth: Array<number> = new Array<number>();
	@observable feeCalculationType: FeeCalculationType = FeeCalculationType.FixedAmount;
	feeValue: number;
	paymentLimitToolip: string;
	feeValues: FeeCalculationValue[];
	isTieredFees: boolean;
	paymentPlanType: PaymentPlanType = PaymentPlanType.Autopay;

	constructor(
		private globalState: State,
		private router: Router,
		private payrazrService: PayrazrService,
		private hpfApi: HPFServiceClient,
		public autopayService: AutopayService,
		private i18n: I18N
	) {
		this.confirmationTitle = this.i18n.i18next.t('completePlanSetUp', 'Please review the below information, enter the Account Holder Name EXACTLY AS IT APPEARS BELOW, and click "I Accept", in order to complete the plan set-up.');
		this.paymentLimitToolip = this.i18n.i18next.t('paymentLimitDescription', 'Enter the amount you wish to set as the autopay amount. Check the "Pay In Full" checkbox to the side if you wish to pay off your entire balance for this account.');
	}

	activate(model) {
		for (let i = 1; i <= 31; i++) {
			this.daysOfMonth.push(i);
		}

		this.account = this.globalState.accounts.find((element, index, array) => {
			return element.id === model.id;
		});

		this.customer = this.globalState.customer;

		if (this.globalState.isConsumerPortalGuarantorBillingEnabled) {
			this.globalState.associatedCustomers.forEach(customer => {
				customer.accounts.forEach(account => {
					if (this.account.customerAccountKey === account.customerAccountKey) {
						this.customer = customer;
					}
				});
			});
		}

		this.portfolio = this.globalState.customer.portfolios.find((element, index, array) => {
			return element.portfolioName === this.account.portfolioName;
		});
		this.autopayService.getLatestAutopay(this.account.customerAccountKey)
			.then((ap) => {
				if (ap) {
					this.showError(this.i18n.i18next.t('errorLoadingPageData', 'Error loading page data'));
					this.isLoading = false;
				} else {

					this.amount = this.account.balance;

					this.configureFeeSettings();
					this.getFrequencies().then(() => {
						this.isLoading = false;
					});
					this.setMaxStartDate();
				}
			});
	}

	getFrequencies(): Promise<any> {
		return this.hpfApi.getPortfolioAutopaySettings(this.portfolio.portfolioKey)
			.then((response) => {
				if (response.isSuccessful) {
					this.availableFrequencies = response.value.autopayTriggerOptions;
					if (response.value.selectableDaysOfMonth) {
						this.daysOfMonth = response.value.selectableDaysOfMonth;
					}
				}
			});
	}

	configureFeeSettings() {
		this.serviceFeeEnabled = this.portfolio.merchantSettings.feeSettings.allowFeesOnAutopays;
	}

	setMaxStartDate() {
		this.minFutureStartDateDays = 0;
		this.maxFutureStartDateDays = this.portfolio.autopaySettings.maxFutureStartDateInDays;
	}

	next(event) {
		this.validationErrors = event.detail.errors;
		this.validate();
		if (this.validationErrors.length === 0) {
			this.paymentInformation = event.detail;
			this.cardNumber = this.paymentInformation.card ? this.paymentInformation.card.cardNumber : null;
			this.cardInfoKey = this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod ? this.paymentInformation.card.selectedPaymentMethod.cardInfoKey : null;
			this.getAutopayInfo();

			if (!this.account.portfolioName) {
				console.error('Portfolio name is empty in PayInRecurringForm');
			}

			this.payrazrService.calculateFee(event.detail.card ? true : false, this.account.portfolioName, event.detail.zipCode, event.detail.amount, this.cardNumber, this.cardInfoKey, [this.account.customerAccountKey])
				.then(result => {
					if (this.serviceFeeEnabled) {
						this.paymentInformation.firstPaymentFee = result.entity.feeValue;
						let feeOnSubsequentPayments = this.globalState.business.portalSettings.feeSettings.allowServiceFeesOnEveryAutopayTransaction ? result.entity.feeValue : 0;
						this.paymentInformation.subsequentPaymentFee = feeOnSubsequentPayments;
						this.feeValue = result.entity.feeValue;
						this.feeCalculationType = result.entity.feeCalculationType;
						this.feeValues = result.entity.feeValues;
					}
					else {
						this.feeValue = 0;
					}

					this.getConfirmationTemplateData(this.feeValues);
					this.showConfirmation();
				})
				.catch(error => {
					alert(this.i18n.i18next.t('unableToCommunicateWithServer', 'Unable to communicate with the server. Please try again.'));
				});
		}
	}

	showConfirmation() {
		this.state = 'confirmation';
	}

	hideConfirmation() {
		this.state = 'setup';
	}

	// Get object used to construct the template
	getConfirmationTemplateData(feeValues: Array<FeeCalculationValue>) {
		let totalAmount: number = numeral(this.calculatedTotalAmount).format('($0,0.00)');

		let endDate = this.paymentInformation.autopay.endDate || 'N/A';
		if (!this.paymentInformation.autopay.numberOfPayments && !this.paymentInformation.autopay.endDate) {
			endDate = 'Indefinite';
		}

		let formattedFeeAmount: string;
		if (this.feeCalculationType === FeeCalculationType.Percentage) {
			formattedFeeAmount = `${numeral(this.paymentInformation.firstPaymentFee).format('0.[0000]')}%`;
		} else {
			formattedFeeAmount = numeral(this.paymentInformation.firstPaymentFee).format('($0,0.00)');
		}

		let customObject: any = {
			paymentAmountLimit: numeral(this.paymentInformation.autopay.maxPaymentAmount).format('($0,0.00)'),
			payInFull: this.paymentInformation.autopay.payInFull,
			accountNumberLabel: this.globalState.business.portalSettings.accountNumberLabel,
			customerAccountNumber: this.account.referenceId,
			accountNumber: this.account.referenceId, // backwards compatibility
			customerAccountName: this.account.name,
			balance: numeral(this.account.balance).format('($0,0.00)'),
			serviceFee: formattedFeeAmount,
			planFrequency: this.paymentInformation.autopay.frequency.description,
			total: totalAmount,
			planStartDate: moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY'),
			planEndDate: endDate,
			numberOfPayments: this.paymentInformation.autopay.numberOfPayments || 'N/A',
			merchantName: this.globalState.business.name,
			paymentScheduleTable: '',
			accountName: this.customer.name,
			zipCode: this.paymentInformation.zipCode,
			paymentMethod: this.paymentMethodDescription,
			customerAccountLocationName: (this.account.bucket) ? this.account.bucket.displayName : '',
			customerAccountLocationReferenceId: (this.account.bucket) ? this.account.bucket.referenceId : '',
			billingPhone: this.globalState.customer.mobilePhone,
			billingEmail: this.globalState.customer.email
		};

		const merchantKey = this.globalState.business.getMerchantKeyAssociatedToPortfolioByPortfolioName(this.account.portfolioName);
		this.confirmationTemplateData = new TemplateData(customObject, merchantKey, null, feeValues);
	}

	get paymentMethodDescription() {
		if (this.paymentInformation.isWallet) {
			if (this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.card.selectedPaymentMethod.cardType} ending ${this.paymentInformation.card.selectedPaymentMethod.last4ofCard}`, paymentMethodTypes: this.paymentInformation.card.selectedPaymentMethod.cardType, accountNumber: this.paymentInformation.card.selectedPaymentMethod.last4ofCard });
			}
			else if (this.paymentInformation.check && this.paymentInformation.check.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.check.selectedPaymentMethod.achAccountType} ending ${this.paymentInformation.check.selectedPaymentMethod.last4ofAccount}`, paymentMethodTypes: this.paymentInformation.check.selectedPaymentMethod.achAccountType, accountNumber: this.paymentInformation.check.selectedPaymentMethod.last4ofAccount });
			}
		}
		else {
			if (this.paymentInformation.isCardPayment){
				return this.paymentInformation.card.description;
			}
			if (this.paymentInformation.isCheckPayment){
				return this.paymentInformation.check.description;
			}
		}
	}

	getAutopayPaymentMethod(): Card|Ach {
		let method: Card|Ach = null;
		let paymentDetails = this.paymentInformation.isCardPayment ? this.paymentInformation.card : this.paymentInformation.check;
		let wallet = paymentDetails.selectedPaymentMethod;

		if (this.paymentInformation.isCardPayment) {
			method = new Card();
			method.paymentMethodType = PaymentMethodType.Card;
			if (wallet) {
				method.paymentMethodKey = wallet.cardInfoKey;
				this.autopayService.savedToWallet = false;
			} else {
				method.accountNumber = paymentDetails.cardNumber;
				method.expirationMonth = paymentDetails.expMonth;
				method.expirationYear = paymentDetails.expYear;
				method.cvv = paymentDetails.cvv;
				this.autopayService.savedToWallet = true;
			}
		} else {
			method = new Ach();
			method.paymentMethodType = PaymentMethodType.ECheck;
			if (wallet) {
				method.paymentMethodKey = wallet.checkInfoKey;
			} else {
				method.accountNumber = paymentDetails.accountNumber;
				method.aba = paymentDetails.transitNumber;

				if (paymentDetails.accountType === 'checking') {
					method.achAccountType = AchAccountType.checking;
				} else if (paymentDetails.accountType === 'savings') {
					method.achAccountType = AchAccountType.savings;
				} else {
					method.achAccountType = AchAccountType.businessChecking;
				}
			}
		}

		method.isThirdParty = this.paymentInformation.isThirdPartyPayment;
		method.accountName = this.paymentInformation.name;
		method.city = this.paymentInformation.billingCity;
		method.state = this.paymentInformation.billingState;
		method.customerKey = this.globalState.customer.customerKey;
		method.isPrimaryPaymentMethod = true;
		method.zipCode = this.paymentInformation.zipCode;

		return method;
	}

	getAutopayInfo() {
		this.paymentInformation.autopay = {
			maxPaymentAmount: this.paymentAmount,
			payInFull: this.payInFull,
			frequency: this.selectedFrequency,
			dayOfMonth: this.dayOfMonth,
			endDate: this.lastPaymentDate || null,
			numberOfPayments: this.lastPaymentPayments || null,
		};
	}

	@computedFrom('availableFrequencies')
	get availableFreqs(): AutopayFrequencyOption[] {
		return this.availableFrequencies;
	}

	@computedFrom('availableFreqs')
	get availableFrequencyDescs(): string[] {
		return this.availableFreqs.map(f => f.description);
	}

	validate() {
		this.validationErrors = this.validationErrors || [];

		if (!this.payInFull && (!this.paymentAmount || parseInt(this.paymentAmount, 10) <= 0)) {
			this.validationErrors.push(this.i18n.i18next.t('selectPaymentAmount', 'Please select a payment amount'));
		}

		if (!this.selectedFrequency) {
			this.validationErrors.push(this.i18n.i18next.t('selectFrequencyOfAutopayPayments', 'Please select the frequency of autopay payments'));
		}

		if (this.endDateOption === 'date' && !this.lastPaymentDate) {
			this.validationErrors.push(this.i18n.i18next.t('selectValidLastPaymentDate', 'Please select a valid last payment date'));
		}
		if (this.endDateOption === 'payments' && this.lastPaymentPayments < 1) {
			this.validationErrors.push(this.i18n.i18next.t('selectValidLastPaymentPayments', 'Please select a valid number of payments'));
		}

		if (this.selectedFrequency
			&& this.selectedFrequency.autopayTriggerType === 5
			&& this.endDateOption === 'date'
			&& this.lastPaymentDate )
		{
			let endDate = moment(this.lastPaymentDate, 'MM/DD/YYYY');
			let endDateDay = endDate.date();
			if (!(endDateDay >= this.dayOfMonth
					|| (this.isLastDayOfMonth(endDate.toDate())
						&& this.dayOfMonth > endDateDay)
				))
			{
				this.validationErrors.push(this.i18n.i18next.t('autoPayErrorEndDateDayOfMonth', 'End date cannot be before first trigger date.'));
			}
		}

	}

	isLastDayOfMonth(date) {
		let month = date.getMonth();
		let newDate = date;
		newDate.setDate(date.getDate() + 1);
		if (newDate.getMonth() !== month) {
			return true;
		}
		return false;
	}

	endDateOptionChanged() {
		if (this.endDateOption === 'indefinite') {
			this.lastPaymentDate = null;
			this.lastPaymentPayments = null;
		} else if (this.endDateOption === 'date') {
			this.lastPaymentPayments = null;
		} else if (this.endDateOption === 'payments') {
			this.lastPaymentDate = null;
		}
	}

	setSelectedEndOption() {
		if (this.lastPaymentDate) {
			this.endDateOption = 'date';
		} else if (this.lastPaymentPayments) {
			this.endDateOption = 'payments';
		} else {
			this.endDateOption = 'indefinite';
		}

	}

	showError(error: string) {
		this.error = error;
	}

	goBack() {
		this.router.navigateToRoute('secure');
	}

	reset() {
		this.validationErrors = new Array();
	}

	submit() {
		const autopay = new Autopay();
		autopay.customerAccountKey = this.account.customerAccountKey;
		autopay.maxPaymentAmount = this.paymentAmount ? numeral(this.paymentAmount).value() : null;
		autopay.payInFull = this.payInFull;
		autopay.frequency = this.selectedFrequency;
		autopay.dayOfMonth = this.dayOfMonth;
		autopay.endDate = this.lastPaymentDate;
		autopay.numberOfPayments = this.lastPaymentPayments;
		autopay.paymentMethod = this.getAutopayPaymentMethod();
		this.autopayService.createAutopay(autopay).then(
			(response) => {
				if (response.isSuccessful) {
					this.autopayService.autopay.key = response.value.autopayKey;
					this.router.navigateToRoute('autopay/autopay-receipt', { id: this.account.id });
				}
				this.isSubmitting = false;
			},
			(error) => {
				this.isSubmitting = false;
				this.showError(this.i18n.i18next.t('anErrorOccurred', 'An Error Occurred'));
				this.isSubmitting = false;
			}
		);
	}
}
